import type { ButtonHTMLAttributes, ComponentType, ReactNode, Ref } from "react";
import { cx, focusRing } from "./cx";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: "sm" | "md";
  readonly icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  readonly children?: ReactNode;
  readonly ref?: Ref<HTMLButtonElement>;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-brand text-brand-ink border-transparent hover:opacity-90",
  secondary: "bg-surface text-ink-1 border-line-strong hover:bg-hover",
  ghost: "bg-transparent text-ink-2 border-transparent hover:bg-hover hover:text-ink-1",
  danger: "bg-tone-danger-bg text-tone-danger-ink border-tone-danger-line hover:opacity-90",
};

export function Button({ variant = "secondary", size = "md", icon: Icon, className, children, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-control border font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "h-8 px-2.5 text-sm" : "h-9 px-3.5 text-sm",
        VARIANT[variant],
        focusRing,
        className,
      )}
      {...rest}
    >
      {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
      {children}
    </button>
  );
}
